import * as Icons from "lucide-react";
import { Search } from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/core/components/ui/button";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/core/components/ui/command";
import {
  filterNavItemsByRoles,
  navItems,
  type NavGroupSubItem,
  type NavLeafSubItem,
  type NavSubItem,
} from "@/core/config/nav-config";
import { useRealmRoles } from "@/core/auth/roles";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";

type CommandSearchItem = {
  icon: ComponentIcon;
  labelKey: TFunctionType;
  href: string;
  openInNewTab?: boolean;
};

type ComponentIcon = Icons.LucideIcon;

type CommandSearchGroup = {
  titleKey: TFunctionType;
  data: CommandSearchItem[];
};

const isLeafSubItem = (item: NavSubItem): item is NavLeafSubItem =>
  "href" in item;

const isGroupSubItem = (item: NavSubItem): item is NavGroupSubItem =>
  "childItems" in item;

const isExternalLink = (href: string) =>
  href.startsWith("http://") || href.startsWith("https://");

// Aplatit la nav-config en groupes de recherche (feuilles uniquement, les
// sous-groupes imbriqués sont aplatis à leur niveau parent).
const buildSearchGroups = (
  groups: typeof navItems
): CommandSearchGroup[] =>
  groups.map((group) => {
    const data: CommandSearchItem[] = [];

    group.items.forEach((item) => {
      if (!item.childItems) {
        data.push({
          icon: Icons[item.icon] as unknown as ComponentIcon,
          labelKey: item.label,
          href: item.href,
          openInNewTab: isExternalLink(item.href),
        });
        return;
      }

      item.childItems.forEach((subItem) => {
        const leaves = isGroupSubItem(subItem) ? subItem.childItems : [subItem];

        leaves.filter(isLeafSubItem).forEach((leaf) => {
          data.push({
            icon: Icons[item.icon] as unknown as ComponentIcon,
            labelKey: leaf.label,
            href: leaf.href,
            openInNewTab: isExternalLink(leaf.href),
          });
        });
      });
    });

    return { titleKey: group.groupLabel ?? "nav.groups.overview", data };
  });

export function CommandMenu() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { t } = useTypedTranslation();

  // Entrées masquées selon les rôles de realm (matrice §6.x du guide).
  const realmRoles = useRealmRoles();
  const searchGroups = useMemo(
    () => buildSearchGroups(filterNavItemsByRoles(navItems, realmRoles)),
    [realmRoles]
  );

  const runCommand = useCallback((command: () => unknown) => {
    setOpen(false);
    command();
  }, []);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || e.key === "/") {
        if (
          (e.target instanceof HTMLElement && e.target.isContentEditable) ||
          e.target instanceof HTMLInputElement ||
          e.target instanceof HTMLTextAreaElement ||
          e.target instanceof HTMLSelectElement
        ) {
          return;
        }

        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);

    return () => document.removeEventListener("keydown", down);
  }, []);

  const suggestions = useMemo(
    () =>
      searchGroups
        .flatMap((group) => group.data)
        .filter((item) => !item.openInNewTab)
        .slice(0, 5),
    [searchGroups]
  );

  return (
    <>
      <Button
        variant="ghost"
        className="hidden px-2.5 font-normal hover:bg-transparent sm:block dark:hover:bg-transparent"
        onClick={() => setOpen(true)}
      >
        <div className="hidden items-center gap-1.5 text-sm text-muted-foreground sm:flex">
          <Search />
          <span>{t("layout.commandMenu.trigger")}</span>
          <kbd className="pointer-events-none inline-flex h-5 select-none items-center rounded border bg-muted px-1.5 font-mono text-[10px] font-medium">
            ⌘K
          </kbd>
        </div>
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="sm:hidden"
        onClick={() => setOpen(true)}
        aria-label={t("layout.commandMenu.trigger")}
      >
        <Search />
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <Command>
          <CommandInput
            placeholder={t("layout.commandMenu.placeholder")}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{t("layout.commandMenu.empty")}</CommandEmpty>
            {search ? (
              searchGroups.map((searchGroup, index) => (
                <Fragment key={searchGroup.titleKey}>
                  <CommandGroup heading={t(searchGroup.titleKey)}>
                    {searchGroup.data.map((item) => (
                      <CommandItem
                        key={`${item.labelKey}-${item.href}`}
                        keywords={[t(item.labelKey)]}
                        onSelect={() =>
                          runCommand(() => {
                            if (item.openInNewTab) {
                              window.open(item.href, "_blank", "noopener,noreferrer");
                            } else {
                              navigate(item.href);
                            }
                          })
                        }
                      >
                        <item.icon />
                        <span>{t(item.labelKey)}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  {index !== searchGroups.length - 1 && <CommandSeparator />}
                </Fragment>
              ))
            ) : (
              <CommandGroup heading={t("layout.commandMenu.suggestions")}>
                {suggestions.map((item) => (
                  <CommandItem
                    key={`${item.labelKey}-${item.href}`}
                    onSelect={() => runCommand(() => navigate(item.href))}
                  >
                    <item.icon />
                    <span>{t(item.labelKey)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  );
}
